import { log } from './log';

export type StreamStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface StreamClientOptions<T> {
  path: string;
  label: string;
  onFrame: (frame: T) => void;
  onStatus?: (status: StreamStatus) => void;
  maxAttempts?: number;
}

const BASE_DELAY_MS = 600;
const MAX_DELAY_MS = 15_000;
const FACTOR = 1.8;

function resolveUrl(path: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}${path}`;
}

export class StreamClient<T> {
  private socket: WebSocket | null = null;
  private timer: number | null = null;
  private attempt = 0;
  private disposed = false;
  private openedAt = 0;

  constructor(private readonly opts: StreamClientOptions<T>) {}

  start(): void {
    if (this.disposed) return;
    this.connect();
  }

  private setStatus(status: StreamStatus): void {
    this.opts.onStatus?.(status);
  }

  private connect(): void {
    if (this.disposed) return;
    const url = resolveUrl(this.opts.path);
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    log.info(
      'ws',
      `${this.opts.label} connecting${this.attempt > 0 ? ` (attempt ${this.attempt + 1})` : ''} -> ${url}`,
    );

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch (err) {
      log.error('ws', `${this.opts.label} constructor threw`, err);
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      if (this.disposed) return;
      this.openedAt = Date.now();
      this.attempt = 0;
      log.info('ws', `${this.opts.label} open`);
      this.setStatus('open');
    };

    socket.onmessage = (event) => {
      if (this.disposed) return;
      try {
        this.opts.onFrame(JSON.parse(event.data as string) as T);
      } catch (err) {
        log.warn('ws', `${this.opts.label} bad frame`, err);
      }
    };

    socket.onerror = () => {
      if (this.disposed) return;
      log.warn('ws', `${this.opts.label} socket error`);
    };

    socket.onclose = (event) => {
      if (this.disposed) return;
      const heldMs = this.openedAt ? Date.now() - this.openedAt : 0;
      log.warn(
        'ws',
        `${this.opts.label} closed code=${event.code} clean=${event.wasClean} held=${heldMs}ms`,
      );
      this.socket = null;
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.disposed) return;
    const max = this.opts.maxAttempts ?? Infinity;
    if (this.attempt >= max) {
      log.error('ws', `${this.opts.label} giving up after ${this.attempt} attempts`);
      this.setStatus('closed');
      return;
    }
    const backoff = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * Math.pow(FACTOR, this.attempt));
    const delay = Math.round(backoff * (0.75 + Math.random() * 0.5));
    this.attempt += 1;
    this.setStatus('reconnecting');
    log.info('ws', `${this.opts.label} reconnect in ${delay}ms`);
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.connect();
    }, delay);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close(1000, 'unmount');
      }
    }
    log.debug('ws', `${this.opts.label} disposed`);
    this.setStatus('closed');
  }
}
